// src/services/historyService.js
import { logger } from "../utils/logger";
import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';

/**
 * Save history entry for both client and helper when a task is completed
 * @param {Object} taskData - The task/problem data
 * @param {string} taskData.taskId - The task ID
 * @param {string} taskData.title - Task title
 * @param {string} taskData.description - Task description
 * @param {string} taskData.domain - Task domain/category
 * @param {string} taskData.clientId - Client user ID
 * @param {string} taskData.helperId - Helper user ID
 * @param {string} taskData.clientName - Client name
 * @param {string} taskData.helperName - Helper name
 * @param {number} taskData.rating - Rating from client (optional)
 * @param {Object} taskData.attachment - Attachment data (optional)
 */
export const saveHistoryForBothUsers = async (taskData) => {
  try {
    const {
      taskId,
      title,
      description,
      domain,
      clientId,
      helperId,
      clientName,
      helperName,
      rating,
      attachment,
      status,
      incidentReportId,
    } = taskData;

    if (!taskId || !clientId) {
      console.error('Missing required fields:', { taskId, clientId, helperId });
      throw new Error('Missing required fields: taskId, clientId');
    }

    // If helperId is missing, we still save to client history but log a warning
    if (!helperId) {
      console.warn('HelperId missing when saving history. Saving only to client history.', taskId);
    }

    const dateCompleted = serverTimestamp();
    const baseHistoryData = {
      taskId,
      title: title || 'Untitled Task',
      description: description || '',
      domain: domain || 'General',
      dateCompleted,
      status: status || 'completed',
      createdAt: taskData.createdAt || dateCompleted,
      assignedAt: taskData.assignedAt || null,
      completedAt: dateCompleted,
      attachment: attachment || null,
      ...(incidentReportId ? { incidentReportId } : {}),
    };

    // Save to client's history (always save to client, even if no helper)
    const clientHistoryRef = doc(
      db,
      'users',
      clientId,
      'history',
      taskId
    );
    try {
      await setDoc(clientHistoryRef, {
        ...baseHistoryData,
        helperName: helperName || 'Unknown Helper',
        helperId: helperId || null,
        rating: rating || null,
        clientName: clientName || 'You',
        clientId,
      });
      logger.log('✅ Saved to client history:', clientId, taskId);
    } catch (clientError) {
      console.error('❌ Failed to save to client history:', clientError);
      throw new Error(`Failed to save client history: ${clientError.message}`);
    }

    // Save to helper's history only if helperId exists
    if (helperId) {
      const helperHistoryRef = doc(
        db,
        'users',
        helperId,
        'history',
        taskId
      );
      try {
        await setDoc(helperHistoryRef, {
          ...baseHistoryData,
          clientName: clientName || 'Unknown Client',
          clientId,
          rating: rating || null,
          helperName: helperName || 'You',
          helperId,
        });
        logger.log('✅ Saved to helper history:', helperId, taskId);
      } catch (helperError) {
        console.error('❌ Failed to save to helper history:', helperError);
        // Don't throw here - at least client history was saved
        console.warn('Continuing despite helper history save failure');
      }
    }

    return { success: true };
  } catch (error) {
    console.error('❌ Error saving history for both users:', error);
    console.error('Task data:', taskData);
    return { success: false, error: error.message };
  }
};

/**
 * Get user's history with optional filtering
 * @param {string} uid - User ID
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by status
 * @param {string} options.domain - Filter by domain
 * @param {number} options.limit - Limit results
 * @returns {Promise<Array>} Array of history items
 */
export const getUserHistory = async (uid, options = {}) => {
  try {
    const historyRef = collection(db, 'users', uid, 'history');
    let q = query(historyRef, orderBy('dateCompleted', 'desc'));

    // Apply filters
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    if (options.domain) {
      q = query(q, where('domain', '==', options.domain));
    }

    const snapshot = await getDocs(q);
    let items = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Apply limit if specified
    if (options.limit) {
      items = items.slice(0, options.limit);
    }

    return items;
  } catch (error) {
    console.error('Error fetching user history:', error);
    return [];
  }
};

/**
 * Calculate summary statistics for a user's history
 * @param {string} uid - User ID
 * @param {string} userRole - 'client' or 'helper'
 * @returns {Promise<Object>} Summary stats
 */
export const calculateSummaryStats = async (uid, userRole = 'client') => {
  try {
    const history = await getUserHistory(uid);

    const totalTasks = history.length;
    // Calculate average rating
    const ratings = history
      .filter((item) => item.rating != null)
      .map((item) => item.rating);
    const avgRating =
      ratings.length > 0
        ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length
        : 0;

    // Domain breakdown
    const domainBreakdown = {};
    history.forEach((item) => {
      const domain = item.domain || 'General';
      domainBreakdown[domain] = (domainBreakdown[domain] || 0) + 1;
    });

    // Monthly completion data (last 6 months)
    const monthlyData = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const monthTasks = history.filter((item) => {
        const itemDate = item.dateCompleted?.toDate
          ? item.dateCompleted.toDate()
          : item.dateCompleted?.seconds
          ? new Date(item.dateCompleted.seconds * 1000)
          : null;
        return itemDate && itemDate >= monthStart && itemDate <= monthEnd;
      });
      monthlyData.push({
        month: monthStart.toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        }),
        count: monthTasks.length,
      });
    }

    return {
      totalTasks,
      avgRating,
      domainBreakdown,
      monthlyData,
    };
  } catch (error) {
    console.error('Error calculating summary stats:', error);
    return {
      totalTasks: 0,
      avgRating: 0,
      domainBreakdown: {},
      monthlyData: [],
    };
  }
};

